import { GrinNotificationStatuses, GrinNotificationBulkActions } from '@grin-rnd/grin-api-sdk/dist/Enums/Notifications'

const notNull = value => !!value

/**
 * Builds a filter object to use for `searchGrinNotifications` query.
 *
 * @param {Object} args
 * @param {Boolean} args.unreadOnly - Whether to fetch only unread notifications or not.
 * @param {Array} args.notificationTypes - Array of notification types to filter by. @see GrinNotificationTypes
 * @param {String} args.grinUserId - Recipient Grin user ID.
 * @param {String} [args.relatedAccountOwnerId] - Related account owner ID.
 * @param {String} args.lastMarkAllAsReadDate - Date of the last time the user marked all notifications as read.
 * @param {String} args.lastClearAllDate - Date of the last time the user cleared all notifications.
 * @returns {Object} AppSync filter object to use for `searchGrinNotifications` query.
 */
export const buildGrinNotificationFilter = ({
  unreadOnly,
  notificationTypes = [],
  grinUserId,
  relatedAccountOwnerId,
  lastMarkAllAsReadDate,
  lastClearAllDate
}) => ({
  and: [
    {
      recipientGrinUserId: {
        eq: grinUserId
      }
    },
    relatedAccountOwnerId && {
      relatedAccountOwnerId: {
        eq: relatedAccountOwnerId
      }
    },
    notificationTypes.length && {
      or: notificationTypes.map(type => ({
        type: { eq: type }
      }))
    },
    // If the user has cleared all notifications, only fetch notifications older than the last time they did.
    lastClearAllDate && {
      eventDate: {
        gte: lastClearAllDate
      }
    },
    unreadOnly
      ? {
          and: [
            {
              status: { eq: GrinNotificationStatuses.New }
            },
            // If the user has marked all notifications as read, only fetch notifications on status "New" after the last time they did, according to their lastStatusUpdateDate.
            // This achieves two things:
            //  1. It prevents showing notitifications on status "New" that created before the user marked all notifications as read (while they are being updated in the background).
            //  2. It allows showing notifications that were marked as unread_after the lastMarkAllAsReadDate.
            lastMarkAllAsReadDate && {
              or: [
                {
                  lastStatusUpdateDate: {
                    gte: lastMarkAllAsReadDate
                  }
                }
              ]
            }
          ].filter(notNull)
        }
      : {
          // If we want all notifications, fetch both "New" and "Read" notifications regardless to the lastMarkAllAsReadDate.
          or: [
            {
              status: { eq: GrinNotificationStatuses.New }
            },
            {
              status: { eq: GrinNotificationStatuses.Read }
            }
          ]
        }
  ].filter(notNull)
})

export const extractBulkActionSettings = appSettingsJson => {
  const appSettings = JSON.parse(appSettingsJson || '{}')
  const bulkActionSettings = appSettings.notificationCenter?.bulkActions || {}

  return {
    lastMarkAllAsReadDate: bulkActionSettings[GrinNotificationBulkActions.MarkAllAsRead]?.lastRequestedAt,
    lastClearAllDate: bulkActionSettings[GrinNotificationBulkActions.ClearAll]?.lastRequestedAt
  }
}

/**
 * Gets the original notification status and calculates the actual status based on the lastClearAllDate and lastMarkAllAsReadDate.
 * @param {Object} args
 * @param {String} args.originalStatus - Original notification status.
 * @param {String} args.lastStatusUpdateDate
 * @param {String} args.lastClearAllDate - Date of the last time the user cleared all notifications.
 * @param {String} args.lastMarkAllAsReadDate - Date of the last time the user marked all notifications as read.
 * @returns {String} Actual notification status.
 */
export const mapNotificationStatus = ({
  originalStatus,
  lastStatusUpdateDate,
  lastClearAllDate,
  lastMarkAllAsReadDate
}) => {
  if (lastClearAllDate && lastStatusUpdateDate < lastClearAllDate) {
    return GrinNotificationStatuses.Deleted
  }

  if (lastMarkAllAsReadDate && lastStatusUpdateDate < lastMarkAllAsReadDate) {
    return GrinNotificationStatuses.Read
  }

  return originalStatus
}
